import { runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useEffect } from 'react'
import { useParams } from 'react-router'
import {
	BaseEventListenerModel,
	EventHandler,
	EventListener,
} from '../../../../api/clients/events/DTO'
import EventHandlersClient from '../../../../api/clients/events/EventHandlersClient'
import EventListenersClient from '../../../../api/clients/events/EventListenersClient'
import EventsClient from '../../../../api/clients/events/EventsClient'
import { FullscreenSpinner } from '../../../../components/feedback/circular'
import AdministrationPageContainer from '../AdministrationPageContainer'
import { ConfigureEventListenerSection } from './ConfigureEventListenerSection'
import { EventListenerLogSection } from './EventListenerLogSection'

export const EventListenerPage = observer(() => {
	const params = useParams<{ eventListenerId: string }>()
	const eventListenerId = parseInt(params.eventListenerId)

	if (isNaN(eventListenerId)) throw new Error('invalid event listener id')

	const localStore = useLocalObservable(() => ({
		eventListener: undefined as EventListener | undefined,
		eventHandler: undefined as EventHandler | undefined,
		existingEventListenerNames: [] as string[],
		eventIds: [] as string[],
		selectedTabIndex: 0,
	}))

	useEffect(() => {
		fetchEventListener()
		fetchExistingEventListenerNames()
		fetchEventIds()
	}, [])

	const eventListenerClient = new EventListenersClient()
	const eventHandlerClient = new EventHandlersClient()
	const eventsClient = new EventsClient()

	const fetchEventListener = async () => {
		const { data: eventListener } = await eventListenerClient.getEventListener(
			eventListenerId,
		)

		const { data: eventHandler } = await eventHandlerClient.getEventHandler(
			eventListener.eventHandlerId,
		)

		runInAction(() => {
			localStore.eventListener = eventListener
			localStore.eventHandler = eventHandler
		})
	}

	const fetchExistingEventListenerNames = async () => {
		const { data: metadata } =
			await eventListenerClient.getEventListenerMetadata()

		runInAction(() => {
			localStore.existingEventListenerNames = metadata.map((v) => v.displayName)
		})
	}

	const fetchEventIds = async () => {
		const { data: eventIds } = await eventsClient.getEvents()

		runInAction(() => {
			localStore.eventIds = eventIds
		})
	}

	const handleUpdateEventListener = async (
		newModel: BaseEventListenerModel,
	) => {
		const { data: updatedEventListener } =
			await eventListenerClient.updateEventListener(eventListenerId, newModel)

		runInAction(() => {
			localStore.eventListener = updatedEventListener
		})
	}

	const tabSelector = () => {
		if (
			localStore.eventListener === undefined ||
			localStore.eventHandler === undefined
		)
			return <FullscreenSpinner />

		switch (localStore.selectedTabIndex) {
			case 0:
				return (
					<ConfigureEventListenerSection
						eventListenerId={eventListenerId}
						eventListener={localStore.eventListener}
						eventHandler={localStore.eventHandler}
						eventIds={localStore.eventIds}
						existingEventListenerNames={localStore.existingEventListenerNames}
						onUpdateEventListener={handleUpdateEventListener}
					/>
				)
			case 1:
				return <EventListenerLogSection />
		}
	}

	return (
		<AdministrationPageContainer
			title="Event Listener"
			tabs={["Configure", "Logs"]}
			onTabChanged={(newValue) => localStore.selectedTabIndex = newValue}
		>
			{tabSelector()}
		</AdministrationPageContainer>
	)
})
